<template>
  <div class="tab-content content-scroll-sm">
    <div v-if="trackingTypeMeta && trackingTypeMeta.iconName" class="tracking-type-indicator">
      <svgicon :name="trackingTypeMeta.iconName" width="14" height="14" />
      <span>{{ trackingTypeMeta.name }} Tracked</span>
    </div>

    <!-- Refresh button -->
    <div v-if="canAutoRefresh" class="refresh-bar">
      <button type="button" @click="onRefresh" :disabled="mapLoading" class="check-location-btn">
        <span class="refresh-icon" :class="{ spinning: mapLoading }">&#8635;</span>
        Refresh Location
      </button>
    </div>

    <!-- Map view -->
    <div class="map-tracking">
      <div v-if="overrideMessage" class="status-msg">
        <span>
          <svgicon name="tracking" width="249" height="176" :original="true" />
          <p class="delivered-msg"><strong>{{ overrideMessage }}</strong></p>
        </span>
      </div>
      <div v-else-if="mapLoading" class="center-loader">
        <Spinner />
      </div>
      <template v-else-if="isMapView && hasPings">
        <iframe :src="trackUrl" frameborder="0" style="border:0" width="100%" :height="mapHeight" />
      </template>
      <div v-else class="status-msg">
        <span v-if="isMapView">
          <svgicon name="empty-location" width="100%" height="300" :original="true" />
          <p>No location data found</p>
        </span>
        <span v-else-if="hasCourierData">
          <svgicon name="empty-location" width="251" height="160" :original="true" />
          <p>Live location not available for courier shipments</p>
        </span>
        <span v-else>
          <svgicon name="empty-location" width="251" height="160" :original="true" />
          <p>Tracking not available for this shipment</p>
        </span>
      </div>
    </div>

    <!-- Stops table -->
    <Table v-if="stopsData.length" :data="stopsData" :stripe="true" class="table-striped bordered mt-20 stops-table" aria-describedby="locationStopsData">
      <table-column
        v-for="column in pickDropOffConstants"
        :label="column.title"
        :width="column.width"
        :key="column.id">
        <template slot-scope="scope">
          <span v-if="scope.row[column.label]">{{ scope.row[column.label] }}</span>
          <span v-else>-</span>
        </template>
      </table-column>
    </Table>

    <!-- Last known location -->
    <div v-if="lastKnownPlace" class="last-known mt-10">
      <svgicon name="icon-location" width="14" height="14" />
      <span>Last known: <strong>{{ lastKnownPlace }}</strong></span>
      <small v-if="lastKnownTime">{{ lastKnownTime }}</small>
    </div>
  </div>
</template>

<script>
import { pickDropOffConstants, trackType } from '@/constants/trackConstants';
import { getLocation, getDistanceRem } from '@common/lib/track';
import { formatDetailedTime, formatLastKnownTime } from '@/lib/Date';

export default {
  name: 'LocationTab',
  props: {
    pings: { type: Array, default: () => [] },
    pickupLocations: { type: Array, default: () => [] },
    dropLocations: { type: Array, default: () => [] },
    isMapView: { type: Boolean, default: false },
    latestLocation: { type: Object, default: () => null },
    trackingType: { type: String, default: null },
    configurations: { type: Object, default: () => ({}) },
    environmentVariables: { type: Object, default: () => ({}) },
    canAutoRefresh: { type: Boolean, default: false },
    hasCourierData: { type: Boolean, default: false },
    mapLoading: { type: Boolean, default: false },
    isOpenLink: { type: Boolean, default: false },
    overrideMessage: { type: String, default: null },
  },
  data() {
    return { pickDropOffConstants };
  },
  computed: {
    trackingTypeMeta() {
      return trackType.find(t => t.value === this.trackingType);
    },
    hasPings() {
      return this.pings && this.pings.length > 0;
    },
    mapHeight() {
      return window.innerWidth <= 768 ? '350' : '450';
    },
    trackUrl() {
      const tripDetails = {
        locations: this.getLocations(this.pings),
        destination: this.getLocations(this.dropLocations, 'destination'),
        source: this.getLocations(this.pickupLocations, 'source'),
        haltThreshold: (this.configurations && this.configurations.track_settings
          && this.configurations.track_settings.halt_threshold) || 2,
        mapSettings: this.environmentVariables && this.environmentVariables.map_settings,
        isOpenLink: true,
      };
      this.$session.set('mapData', tripDetails);
      return `${window.location.origin}/track/map`;
    },
    stopsData() {
      const stops = [];
      const hidePickup = !this.isOpenLink && this.configurations
        && this.configurations.track_settings
        && this.configurations.track_settings.hide_pickup_from_track;
      if (this.pickupLocations && !hidePickup) {
        this.pickupLocations.forEach(val => {
          stops.push({
            stopType: 'Pickup',
            location: getLocation(val.address),
            estimatedArrival: formatDetailedTime(val.estimatedArrival),
            plannedArrival: formatDetailedTime(val.plannedArrival),
            distanceRemained: getDistanceRem(val.distanceRemained),
          });
        });
      }
      if (this.dropLocations) {
        this.dropLocations.forEach(val => {
          stops.push({
            stopType: 'Drop Off',
            location: getLocation(val.address),
            estimatedArrival: formatDetailedTime(val.estimatedArrival),
            plannedArrival: formatDetailedTime(val.plannedArrival),
            distanceRemained: getDistanceRem(val.distanceRemained),
          });
        });
      }
      return stops;
    },
    lastKnownPlace() {
      return this.latestLocation && (this.latestLocation.city || this.latestLocation.address);
    },
    lastKnownTime() {
      return this.latestLocation && this.latestLocation.timeStamp
        ? formatLastKnownTime(this.latestLocation.timeStamp) : null;
    },
  },
  methods: {
    onRefresh() {
      this.$emit('refresh');
    },
    getLocations(arr, markerValue = null) {
      if (!arr) return [];
      return arr.map((val, index) => {
        const obj = {};
        if (markerValue && val.address) {
          obj[markerValue] = val.address.name;
        }
        return Object.assign(obj, {
          lat: val.lat || (val.address && val.address.lat),
          lon: val.lon || (val.address && val.address.lon),
          createdAt: val.created_at,
          trackingType: val.trackingType || null,
          address: val.address || val.city || null,
        });
      });
    },
  },
};
</script>

<style scoped>
.refresh-bar { text-align: right; margin-bottom: 10px; }
.check-location-btn {
  padding: 10px 20px;
  background-color: #007fea;
  font-size: 1.4rem;
  color: white;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.check-location-btn:disabled { opacity: 0.7; cursor: not-allowed; }
.refresh-icon { font-size: 1.6rem; display: inline-block; line-height: 1; }
.refresh-icon.spinning { animation: spin 0.7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.center-loader { display: flex; justify-content: center; align-items: center; height: 450px; }
.last-known {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; color: #606266;
  small { margin-left: 4px; color: #909399; }
}
.stops-table { overflow-x: auto; }
.delivered-msg { font-size: 16px; font-weight: 700; color: #2e7d32; }
@media (max-width: 768px) {
  .center-loader { height: 350px; }
}
</style>
